import React, { useEffect, useMemo, useState } from "react";
import { Select, Input, Segmented, Button, Table, Modal, Spin, Tooltip, Tag } from "antd";
import { FiArrowUp, FiArrowDown, FiInfo } from "react-icons/fi";
import { toast } from "react-toastify";
import DetailSection from "../commoncomponent/DetailSection";
import PermissionGate from "../commoncomponent/PermissionGate";
import {
  getCoinList,
  getUserFundingWalletsApi,
  manualWalletAdjustmentApi,
  sendWalletAdjustmentOtpApi,
  getWalletAdjustmentHistoryApi,
} from "../../../service/api";

const FieldLabel = ({ children, hint }) => (
  <div style={{ fontSize: 12, fontWeight: 600, color: "#444", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
    {children}
    {hint && (
      <Tooltip title={hint}>
        <FiInfo size={12} style={{ color: "#999", cursor: "help" }} />
      </Tooltip>
    )}
  </div>
);

// Truncates long table text with an ellipsis, showing the full value on
// hover — needed for Reason (up to 500 chars) so long entries don't blow up
// the row height or wrap awkwardly.
const EllipsisText = ({ text, maxWidth = 220 }) => {
  if (!text || text === "-") return <span style={{ color: "#aaa" }}>-</span>;
  return (
    <Tooltip title={text}>
      <span
        style={{
          display: "inline-block",
          maxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          verticalAlign: "bottom",
        }}
      >
        {text}
      </span>
    </Tooltip>
  );
};

// Manual Wallet Adjustment — admin credits/debits a user's FUNDING wallet for
// any coin (e.g. crediting a BTC reward), with a mandatory reason. Balance
// can never go negative — enforced server-side; this tab also blocks an
// over-large debit client-side once the current balance is known, so the
// admin sees the problem before it round-trips to the API.
//
// Standalone component (not an inline closure) — same reasoning as
// UserActivityLogTab in index.jsx: an inline closure is a new component type
// on every parent render and remounts on every keystroke, dropping focus.
const WalletAdjustmentTab = ({ userId, active }) => {
  const [coins, setCoins] = useState([]);
  const [coinsLoading, setCoinsLoading] = useState(false);
  const [fundingWallets, setFundingWallets] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [coinId, setCoinId] = useState(undefined);
  const [type, setType] = useState("CREDIT");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // OTP confirm modal. The OTP is bound to the exact coin/direction/amount/reason
  // it was sent for, so editing any field (or closing the modal) discards it.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [expiresAt, setExpiresAt] = useState(0);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const loadCoins = () => {
    if (coins.length) return;
    setCoinsLoading(true);
    getCoinList({})
      .then((res) => setCoins(res?.data?.result ?? []))
      .catch(() => toast.error("Failed to load coins"))
      .finally(() => setCoinsLoading(false));
  };

  const loadFundingWallets = () => {
    setBalanceLoading(true);
    getUserFundingWalletsApi(userId)
      .then((res) => setFundingWallets(res?.data ?? []))
      .catch(() => toast.error("Failed to load current balances"))
      .finally(() => setBalanceLoading(false));
  };

  const loadHistory = () => {
    setHistoryLoading(true);
    getWalletAdjustmentHistoryApi({ userId, sizePerPage: 10 })
      .then((res) => setHistory(res?.data?.result ?? res?.data ?? []))
      .catch(() => toast.error("Failed to load adjustment history"))
      .finally(() => setHistoryLoading(false));
  };

  useEffect(() => {
    if (!active || loadedOnce) return;
    setLoadedOnce(true);
    loadCoins();
    loadFundingWallets();
    loadHistory();
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const coinOptions = useMemo(
    () => coins.map((c) => ({ value: c._id, label: `${c.symbol} — ${c.name}` })),
    [coins]
  );

  const selectedCoin = useMemo(() => coins.find((c) => c._id === coinId), [coins, coinId]);

  const currentBalance = useMemo(() => {
    if (!coinId) return null;
    const w = fundingWallets.find((w) => (w?.coin?._id ?? w?.coin) === coinId);
    return Number(w?.amount) || 0;
  }, [fundingWallets, coinId]);

  const parsedAmount = Number(amount);
  const amountValid = amount !== "" && Number.isFinite(parsedAmount) && parsedAmount > 0;

  // Live, on-the-fly validation — recomputed on every keystroke/direction
  // change, not just on submit, so an over-large decrease is flagged the
  // moment it's typed instead of after the admin clicks through a confirm.
  const amountError = useMemo(() => {
    if (amount === "") return "";
    if (!amountValid) return "Enter an amount greater than 0";
    if (type === "DEBIT" && currentBalance !== null && parsedAmount > currentBalance) {
      return `Cannot decrease more than the current balance (${currentBalance})`;
    }
    return "";
  }, [amount, amountValid, type, currentBalance, parsedAmount]);

  const afterBalance =
    currentBalance !== null && amountValid && !amountError
      ? currentBalance + (type === "CREDIT" ? parsedAmount : -parsedAmount)
      : null;

  const canSubmit = !!coinId && amountValid && !amountError;

  const validate = () => {
    let ok = canSubmit;
    if (!reason.trim()) {
      setReasonError("A reason is required");
      ok = false;
    } else {
      setReasonError("");
    }
    return ok;
  };

  const resetOtp = () => {
    setOtpSent(false);
    setOtp("");
    setOtpError("");
    setExpiresAt(0);
    setResendAt(0);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    resetOtp();
  };

  // Any change to the adjustment invalidates a sent OTP (server hash is bound to it).
  useEffect(() => {
    if (confirmOpen) closeConfirm();
  }, [coinId, type, amount, reason]); // eslint-disable-line react-hooks/exhaustive-deps

  // 1s tick while the modal is open, drives the expiry / resend countdowns.
  useEffect(() => {
    if (!confirmOpen) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [confirmOpen]);

  const secondsLeft = otpSent ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

  const adjustmentPayload = () => ({
    userId,
    coinId,
    type,
    amount: parsedAmount,
    reason: reason.trim(),
  });

  const openConfirm = () => {
    if (!coinId) {
      toast.warn("Select a coin first");
      return;
    }
    if (!validate()) return;
    resetOtp();
    setConfirmOpen(true);
  };

  const handleSendOtp = async () => {
    if (otpSending) return;
    setOtpSending(true);
    setOtpError("");
    setOtp("");
    try {
      const res = await sendWalletAdjustmentOtpApi(adjustmentPayload());
      const t = Date.now();
      setNow(t);
      setExpiresAt(t + (res?.data?.expiresInSec ?? 60) * 1000);
      setResendAt(t + (res?.data?.resendAfterSec ?? 15) * 1000);
      setOtpSent(true);
      toast.success(res?.message || "OTP sent to admin email", { toastId: "wallet-adjust-otp-sent" });
    } catch (e) {
      setOtpError(e?.data?.message || e?.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpSending(false);
    }
  };

  const handleSubmit = async () => {
    if (!coinId) {
      toast.warn("Select a coin first");
      return;
    }
    if (!validate()) return;
    if (!/^\d{6}$/.test(otp)) {
      setOtpError("Enter the 6-digit OTP sent to the admin email");
      return;
    }

    setSubmitting(true);
    try {
      const res = await manualWalletAdjustmentApi({ ...adjustmentPayload(), otp });
      toast.success(res?.message || "Wallet balance adjusted");
      setAmount("");
      setReason("");
      closeConfirm();
      loadFundingWallets();
      loadHistory();
    } catch (e) {
      const msg = e?.data?.message || e?.response?.data?.message || "Failed to adjust balance";
      // The OTP is single-use / may be expired or locked out — show it next to
      // the code box so the admin can resend; anything else is a plain toast.
      if (/otp/i.test(msg)) setOtpError(msg);
      else toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseMax = () => {
    if (currentBalance) setAmount(String(currentBalance));
  };

  const historyColumns = [
    {
      title: "Date/Time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
    { title: "Coin", dataIndex: "coinSymbol", key: "coinSymbol", width: 80, render: (v) => <Tag>{v}</Tag> },
    {
      title: "Direction",
      dataIndex: "entryType",
      key: "entryType",
      width: 110,
      render: (v) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: v === "CREDIT" ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
          {v === "CREDIT" ? <FiArrowUp size={13} /> : <FiArrowDown size={13} />}
          {v === "CREDIT" ? "Credit" : "Debit"}
        </span>
      ),
    },
    { title: "Amount", dataIndex: "amount", key: "amount", width: 100 },
    {
      title: "Balance (Before → After)",
      key: "balance",
      width: 160,
      render: (_, r) => (
        <span>
          {r?.balanceBefore ?? "-"} <span style={{ color: "#999" }}>→</span> <b>{r?.balanceAfter ?? "-"}</b>
        </span>
      ),
    },
    {
      title: "Reason",
      key: "reason",
      render: (_, r) => <EllipsisText text={r?.reason ?? r?.note} maxWidth={280} />,
    },
    {
      title: "Admin",
      dataIndex: "adminEmail",
      key: "adminEmail",
      width: 200,
      render: (v) => <EllipsisText text={v} maxWidth={180} />,
    },
  ];

  return (
    <div style={{ padding: "16px 0" }}>
      <DetailSection title="Manual Wallet Adjustment">
        <div style={{ padding: "20px 20px 0" }}>
          <p style={{ color: "#666", fontSize: 13, marginBottom: 28, lineHeight: 1.6 }}>
            Credit or debit a specific coin in this user&apos;s Funding Wallet. The balance can never go
            negative, and a reason is required — every adjustment is recorded and also appears in the
            Wallet Activity Log.
          </p>

        <PermissionGate
          module="fund-transfer"
          action="create"
          fallback={<p style={{ color: "#888", fontSize: 13 }}>You don&apos;t have permission to adjust wallet balances.</p>}
        >
          <div
            style={{
              marginBottom: 24,
              padding: 24,
              background: "#fafbfc",
              border: "1px solid #eef0f2",
              borderRadius: 10,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(220px, 1fr) minmax(200px, 1fr) minmax(180px, 1fr)",
                gap: 24,
                marginBottom: 20,
              }}
            >
              <div>
                <FieldLabel>Coin</FieldLabel>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select coin"
                  size="large"
                  style={{ width: "100%" }}
                  loading={coinsLoading}
                  value={coinId}
                  onChange={(v) => setCoinId(v)}
                  options={coinOptions}
                  onFocus={loadCoins}
                />
                <div style={{ fontSize: 12, color: "#666", marginTop: 8, minHeight: 18 }}>
                  {coinId ? (
                    balanceLoading ? (
                      <Spin size="small" />
                    ) : (
                      <>
                        Current balance: <b style={{ color: "#222" }}>{currentBalance ?? 0}</b> {selectedCoin?.symbol}
                      </>
                    )
                  ) : (
                    <span style={{ color: "#bbb" }}>Select a coin to see the current balance</span>
                  )}
                </div>
              </div>

              <div>
                <FieldLabel hint="Increase credits the wallet; Decrease debits it. The balance can never drop below zero.">
                  Direction
                </FieldLabel>
                <Segmented
                  size="large"
                  block
                  value={type}
                  onChange={(v) => setType(v)}
                  options={[
                    { label: <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}><FiArrowUp /> Increase</span>, value: "CREDIT" },
                    { label: <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}><FiArrowDown /> Decrease</span>, value: "DEBIT" },
                  ]}
                />
              </div>

              <div>
                <FieldLabel>Amount</FieldLabel>
                <Input
                  size="large"
                  value={amount}
                  status={amountError ? "error" : undefined}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  suffix={selectedCoin?.symbol}
                />
                <div style={{ marginTop: 8, minHeight: 18 }}>
                  {amountError ? (
                    <span style={{ color: "#dc2626", fontSize: 12 }}>{amountError}</span>
                  ) : afterBalance !== null ? (
                    <span style={{ fontSize: 12, color: "#666" }}>
                      {currentBalance} <span style={{ color: "#999" }}>→</span>{" "}
                      <b style={{ color: type === "CREDIT" ? "#16a34a" : "#dc2626" }}>{afterBalance}</b>
                    </span>
                  ) : type === "DEBIT" && currentBalance !== null && currentBalance > 0 ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={handleUseMax}
                      style={{ fontSize: 12, color: "#1677ff", cursor: "pointer" }}
                    >
                      Use max ({currentBalance})
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <FieldLabel>
                Reason <span style={{ color: "#dc2626" }}>*</span>
              </FieldLabel>
              <Input.TextArea
                rows={2}
                value={reason}
                status={reasonError ? "error" : undefined}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (reasonError) setReasonError("");
                }}
                maxLength={500}
                showCount
                placeholder="e.g. Loyalty reward for referral campaign"
              />
              {reasonError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{reasonError}</div>}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                size="large"
                danger={type === "DEBIT"}
                disabled={!canSubmit}
                onClick={openConfirm}
                icon={type === "CREDIT" ? <FiArrowUp /> : <FiArrowDown />}
              >
                {type === "CREDIT" ? "Increase Balance" : "Decrease Balance"}
              </Button>
            </div>
          </div>
        </PermissionGate>
        </div>

        <div style={{ padding: "0 20px 20px" }}>
          <Table
            rowKey={(r, i) => r?._id ?? i}
            size="small"
            title={() => <span style={{ fontWeight: 600 }}>Recent Manual Adjustments</span>}
            columns={historyColumns}
            dataSource={history}
            loading={historyLoading}
            pagination={false}
            locale={{ emptyText: "No manual adjustments yet" }}
            scroll={{ x: true }}
          />
        </div>

      <Modal
        open={confirmOpen}
        onCancel={closeConfirm}
        title={`Confirm ${type === "CREDIT" ? "balance increase" : "balance decrease"}`}
        maskClosable={false}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={closeConfirm}>Cancel</Button>,
          !otpSent ? (
            <Button key="send" type="primary" danger={type === "DEBIT"} loading={otpSending} onClick={handleSendOtp}>
              Send OTP
            </Button>
          ) : (
            <Button
              key="confirm"
              type="primary"
              danger={type === "DEBIT"}
              loading={submitting}
              disabled={otp.length !== 6 || secondsLeft === 0}
              onClick={handleSubmit}
            >
              {type === "CREDIT" ? "Increase Balance" : "Decrease Balance"}
            </Button>
          ),
        ]}
      >
        <div style={{ fontSize: 13, lineHeight: "22px" }}>
          <div><strong>Coin:</strong> {selectedCoin?.symbol}</div>
          <div><strong>Direction:</strong> {type === "CREDIT" ? "Increase" : "Decrease"}</div>
          <div><strong>Amount:</strong> {amount} {selectedCoin?.symbol}</div>
          {afterBalance !== null && (
            <div><strong>Balance:</strong> {currentBalance} → {afterBalance}</div>
          )}
          <div style={{ wordBreak: "break-word" }}><strong>Reason:</strong> {reason.trim()}</div>
        </div>

        {type === "DEBIT" && (
          <div style={{ marginTop: 12, background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#991B1B", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
            <strong>Balance decrease.</strong> This removes {amount} {selectedCoin?.symbol} from the user's Funding wallet.
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          {otpSent && (
            <>
              <FieldLabel>Enter OTP <span style={{ color: "#dc2626" }}>*</span></FieldLabel>
              <Input
                autoFocus
                maxLength={6}
                inputMode="numeric"
                placeholder="6-digit code from email"
                value={otp}
                status={otpError ? "error" : undefined}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                  if (otpError) setOtpError("");
                }}
                onPressEnter={() => otp.length === 6 && handleSubmit()}
              />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 12 }}>
                <span style={{ color: secondsLeft === 0 ? "#dc2626" : "#666" }}>
                  {secondsLeft === 0 ? "OTP expired — request a new one" : `Expires in ${secondsLeft}s`}
                </span>
                <Button type="link" size="small" style={{ padding: 0, fontSize: 12 }} disabled={resendIn > 0 || otpSending} loading={otpSending} onClick={handleSendOtp}>
                  {resendIn > 0 ? `Resend OTP in ${resendIn}s` : "Resend OTP"}
                </Button>
              </div>
            </>
          )}
          {otpError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{otpError}</div>}
        </div>
      </Modal>
      </DetailSection>
    </div>
  );
};

export default WalletAdjustmentTab;
